/**
 * 安全工具函数
 * 用于检查应用是否在安全的Shopify环境中运行
 */

export interface SecurityCheckResult {
  isInIframe: boolean
  isShopifyDomain: boolean
  isSecure: boolean
  error?: string
  details?: {
    parentHostname?: string
    currentHostname?: string
    userAgent?: string
  }
}

// 缓存初始安全检查结果，避免路由跳转后重复检查
let cachedSecurityResult: SecurityCheckResult | null = null
let initialCheckDone = false

/**
 * 检查当前环境是否安全
 * @returns SecurityCheckResult 安全检查结果
 */
export function checkSecurity(): SecurityCheckResult {
  // 如果已经进行过初始检查且结果是安全的，直接返回缓存结果
  // 避免路由跳转后URL参数丢失导致重复检查失败
  if (initialCheckDone && cachedSecurityResult?.isSecure) {
    return cachedSecurityResult
  }
  const result: SecurityCheckResult = {
    isInIframe: false,
    isShopifyDomain: false,
    isSecure: false,
    details: {
      currentHostname: window.location.hostname,
      userAgent: navigator.userAgent
    }
  }

  try {
    // Shopify域名检查
    const validShopifyDomains = [
      '.myshopify.com',
      '.shopify.com',
      'admin.shopify.com',
      'partners.shopify.com'
    ]

    // 检查是否在iframe中运行
    if (window.self !== window.top) {
      result.isInIframe = true
    }

    // 检查父窗口域名
    try {
      const parentHostname = window.parent.location.hostname
      result.details!.parentHostname = parentHostname

      const isShopifyDomain = validShopifyDomains.some(domain => {
        return parentHostname.endsWith(domain) || parentHostname === domain
      })

      if (isShopifyDomain) {
        result.isShopifyDomain = true
      }
    } catch (e) {
      // 跨域错误，说明不在Shopify环境中
    }

    // 检查当前域名是否也是Shopify域名（作为备用检查）
    const currentHostname = window.location.hostname
    const isCurrentDomainShopify = validShopifyDomains.some(domain => {
      return currentHostname.endsWith(domain) || currentHostname === domain
    })

    if (isCurrentDomainShopify) {
      result.isShopifyDomain = true
    }

    // 检查URL参数中的Shopify标识
    const urlParams = new URLSearchParams(window.location.search)
    const shopifyParams = {
      shop: urlParams.get('shop'),
      host: urlParams.get('host'),
      embedded: urlParams.get('embedded'),
      app_load_id: urlParams.get('app_load_id'),
      hmac: urlParams.get('hmac'),
      id_token: urlParams.get('id_token')
    }

    // 检查是否有Shopify相关的URL参数
    const hasShopifyParams = shopifyParams.shop || shopifyParams.host || shopifyParams.embedded === '1' || shopifyParams.app_load_id || shopifyParams.hmac || shopifyParams.id_token

    if (hasShopifyParams) {
      result.isShopifyDomain = true
    }

    // 检查host参数是否包含Shopify域名
    if (shopifyParams.host) {
      try {
        const decodedHost = atob(shopifyParams.host)

        const isHostShopify = validShopifyDomains.some(domain => {
          return decodedHost.includes(domain.replace('.', '')) || decodedHost.includes(domain)
        })

        if (isHostShopify) {
          result.isShopifyDomain = true
        }
      } catch (e) {
        // 无法解码host参数
      }
    }

    // 检查是否为安装过程
    // 安装过程的特征：
    // 1. 不在iframe中
    // 2. 有hmac参数
    // 3. 没有embedded参数或embedded不为1
    // 4. 没有app_load_id参数（安装时通常没有这个参数）
    const isInstallationProcess = !result.isInIframe &&
      shopifyParams.hmac &&
      shopifyParams.embedded !== '1' &&
      !shopifyParams.app_load_id

    // 检查是否为外部直接访问（从iframe截取的URL）
    // 外部直接访问的特征：
    // 1. 不在iframe中
    // 2. 有hmac参数
    // 3. 有embedded=1参数
    // 4. 有app_load_id参数
    const isExternalDirectAccess = !result.isInIframe &&
      shopifyParams.hmac &&
      shopifyParams.embedded === '1' &&
      shopifyParams.app_load_id

    // 安全检查逻辑：
    // 1. 如果在iframe中运行且具有Shopify标识 -> 安全
    // 2. 如果是真正的安装过程 -> 安全
    // 3. 如果是外部直接访问（从iframe截取的URL） -> 不安全
    // 4. 其他情况 -> 不安全
    if (result.isInIframe && result.isShopifyDomain) {
      result.isSecure = true
    } else if (isInstallationProcess && !isExternalDirectAccess) {
      result.isSecure = true
    } else if (isExternalDirectAccess) {
      result.isSecure = false
    } else {
      result.isSecure = false
    }

    if (!result.isSecure) {
      if (isExternalDirectAccess) {
        result.error = "检测到外部直接访问，此应用只能在Shopify后台中运行"
      } else if (!result.isInIframe && !isInstallationProcess) {
        result.error = "此应用必须在Shopify后台的iframe中运行"
      } else if (!result.isShopifyDomain) {
        result.error = "此应用只能在Shopify后台中运行"
      } else {
        result.error = "此应用只能在Shopify后台中运行"
      }
    }

    // 缓存结果
    if (!initialCheckDone) {
      initialCheckDone = true
      cachedSecurityResult = result
    }

    return result
  } catch (error) {
    result.error = `安全检查失败: ${error}`
    console.error('❌ 安全检查异常:', error)
    
    // 缓存异常结果
    if (!initialCheckDone) {
      initialCheckDone = true
      cachedSecurityResult = result
    }
    
    return result
  }
}

/**
 * 强制执行安全检查
 * 如果不在安全环境中，将显示空白页面
 */
export function enforceSecurity(): void {
  console.log('🔍 开始强制执行安全检查...')

  // 白名单域名配置
  const whiteListDomains = [
    'localhost',
    '127.0.0.1',
    '192.168.',
    '10.',
    '172.16.',
    '172.17.',
    '172.18.',
    // 生产环境域名
    'app.ouyicartapp.com',
    'app-dev.ouyicartapi.com',
    'app-uat.ouyicartapi.com',
    'app-dev.wenfunnels.com',
    'app-uat.wenfunnels.com',
    'app.wenfunnels.com',
    // 开发环境域名
    'frontend.ouyicartapp.com',
    'frontend-dev.ouyicartapi.com',
    'frontend-uat.ouyicartapi.com',
    'frontend.wenfunnels.com',
    'frontend-dev.wenfunnels.com',
    // 测试环境域名
    'test.ouyicartapi.com',
    'staging.ouyicartapi.com',
    // 其他允许的域名
    '.local',
    '.dev',
    '.test',
    'apply-dev.ouyicartapi.com'
  ]

  // 检查当前域名是否在白名单中
  const currentHostname = window.location.hostname
  const isWhitelisted = whiteListDomains.some(domain => {
    if (domain.startsWith('.') && currentHostname.endsWith(domain)) {
      return true
    }
    if (domain.endsWith('.') && currentHostname.startsWith(domain)) {
      return true
    }
    return currentHostname === domain
  })

  // 检查是否为本地开发环境
  const isLocalDevelopment = currentHostname === 'localhost' ||
    currentHostname === '127.0.0.1' ||
    currentHostname.startsWith('192.168.') ||
    currentHostname.startsWith('10.') ||
    currentHostname.endsWith('.local')

  if (isLocalDevelopment) {
    console.log('🔧 本地开发环境：跳过安全检查')
    return
  }

  // 检查是否在白名单域名中
  if (isWhitelisted) {
    console.log('✅ 域名在白名单中：跳过安全检查')
    return
  }

  const securityResult = checkSecurity()

  if (!securityResult.isSecure) {
    console.log('❌ 安全检查失败，显示空白页面')
    console.log('🚫 当前域名不在白名单中:', currentHostname)
    // 显示空白页面
    document.body.innerHTML = ''
    document.body.style.backgroundColor = '#ffffff'

    // 阻止所有脚本执行
    throw new Error('Security check failed: Application must run in Shopify environment or whitelisted domain')
  } else {
    console.log('✅ 安全检查通过，允许应用运行')
  }
}

/**
 * 安全检查（所有环境都执行）
 */
export function checkSecurityDev(): void {
  // 所有环境都强制执行安全检查
  enforceSecurity()
}

/**
 * 持续监控安全状态
 * 定期检查环境是否仍然安全
 */
export function startSecurityMonitoring(intervalMs: number = 30000): () => void {
  const intervalId = setInterval(() => {
    const securityResult = checkSecurity()
    if (!securityResult.isSecure) {
      console.error('安全监控检测到不安全的环境:', securityResult)
      enforceSecurity()
    }
  }, intervalMs)

  // 返回停止监控的函数
  return () => clearInterval(intervalId)
}

/**
 * 获取当前安全状态
 * 用于在组件中检查安全状态
 */
export function getCurrentSecurityStatus(): SecurityCheckResult {
  return checkSecurity()
}
